// client/src/components/pages/Geofencing/GeofenceStatsCards.tsx (converted from .js, ST-00210)
//
// The LexiStar stat strip: zones, vehicles online, pending alerts, events today. Since the
// live map moved to its own page (Live View, ST-00210) each page picks the cards that fit it
// (`stats`): Geofences shows zones + alerts + events, Live View vehicles + alerts + events.
// A card with an entry in `links` opens that page (Live View → the Geofences Events tab).
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FaDrawPolygon, FaTruck, FaBell, FaCalendarDay } from 'react-icons/fa';

export type GeofenceStatKey = 'geofences' | 'vehicles' | 'unacknowledged' | 'eventsToday';

interface StatMeta {
  key: GeofenceStatKey;
  label: string;
  icon: ReactNode;
  color: string;
  bg: string;
}

const STATS: readonly StatMeta[] = [
  {
    key: 'geofences',
    label: 'Zones',
    icon: <FaDrawPolygon />,
    color: 'text-teal-600',
    bg: 'bg-teal-50',
  },
  {
    key: 'vehicles',
    label: 'Vehicles Online',
    icon: <FaTruck />,
    color: 'text-primary-600',
    bg: 'bg-primary-50',
  },
  {
    key: 'unacknowledged',
    label: 'Pending Alerts',
    icon: <FaBell />,
    color: 'text-red-600',
    bg: 'bg-red-50',
  },
  {
    key: 'eventsToday',
    label: 'Events Today',
    icon: <FaCalendarDay />,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
  },
];

const ALL_STATS: readonly GeofenceStatKey[] = STATS.map((s) => s.key);

// Static class names (Tailwind only keeps what it can find in the source).
const GRID_COLUMNS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-2 md:grid-cols-4',
};

export interface GeofenceStatsCardsProps {
  geofenceCount?: number;
  vehicleCount?: number;
  unacknowledgedCount?: number;
  eventsToday?: number;
  /** Replaces "Vehicles Online" (e.g. "Trucks Online" while the Fleet filter is on). */
  vehiclesLabel?: string;
  /** The cards to show, in this order (default: all four). */
  stats?: readonly GeofenceStatKey[];
  /** A card with a path here is a link to it. */
  links?: Partial<Record<GeofenceStatKey, string>>;
}

export default function GeofenceStatsCards({
  geofenceCount = 0,
  vehicleCount = 0,
  unacknowledgedCount = 0,
  eventsToday = 0,
  vehiclesLabel,
  stats = ALL_STATS,
  links = {},
}: GeofenceStatsCardsProps) {
  const values: Record<GeofenceStatKey, number> = {
    geofences: geofenceCount,
    vehicles: vehicleCount,
    unacknowledged: unacknowledgedCount,
    eventsToday,
  };
  const shown = stats
    .map((key) => STATS.find((s) => s.key === key))
    .filter((s): s is StatMeta => s !== undefined);
  // Three across fits a phone only without the icon tiles.
  const iconClass = shown.length === 3 ? 'hidden sm:flex' : 'flex';

  return (
    <div className={`grid ${GRID_COLUMNS[shown.length] ?? GRID_COLUMNS[4]} gap-2 sm:gap-3`}>
      {shown.map((stat) => {
        const label = stat.key === 'vehicles' && vehiclesLabel ? vehiclesLabel : stat.label;
        const body = (
          <>
            <div
              className={`flex-shrink-0 w-10 h-10 rounded-lg items-center justify-center ${iconClass} ${stat.bg} ${stat.color}`}
              aria-hidden="true"
            >
              {stat.icon}
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-gray-900 tabular-nums">
                {values[stat.key]}
              </p>
              <p className="text-xs text-gray-500 leading-tight">{label}</p>
            </div>
          </>
        );
        const cardClass =
          'bg-surface rounded-lg border border-gray-200 p-3 sm:p-4 flex items-center gap-3 min-w-0';
        const to = links[stat.key];
        return to ? (
          <Link
            key={stat.key}
            to={to}
            className={`${cardClass} transition-colors hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`}
          >
            {body}
          </Link>
        ) : (
          <div key={stat.key} className={cardClass}>
            {body}
          </div>
        );
      })}
    </div>
  );
}
